/**
 * Adaptér nad `IngotConfirm` (KAN-583) — držel dřív vlastní overlay.
 *
 * Do KAN-583 to byla plná implementace potvrzovacího dialogu s vlastním
 * `fixed inset-0`. Kostra i veto se přestěhovaly do `@/ingot/IngotConfirm`,
 * tady zůstaly jen dvě věci, které do generického primitiva nepatří:
 *
 * 1. **`open` jako prop.** `IngotModal` se řídí tím, jestli je vůbec
 *    vyrenderovaný (mount = otevřeno), ale 13 volajících tenhle dialog
 *    renderuje natvrdo a spíná ho `open`. Přepsat je by byl sweep, který
 *    KAN-583 výslovně nechce.
 * 2. **Výchozí popisky přes i18n.** Ingot nemá vlastní namespace, takže
 *    fallback „Smazat" / „Zrušit" musí dodat někdo z aplikace.
 *
 * Volající se nemění: `open`, `busy`, `impact`, `testId` i `data-testid`
 * kontrakt (`…-confirm`, `…-cancel`, `…-impact`, `…-blocked`) platí dál.
 * Co se změnilo, je chování k lepšímu — dialog teď nese a11y laťku z KAN-580
 * (focus trap, scroll lock, návrat fokusu na spouštěč, portál do `body`),
 * kterou si tahle komponenta sama nedělala.
 */

import { type JSX, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { IngotConfirm } from "@/ingot";

interface Props {
  /** When false the dialog isn't rendered at all. */
  open: boolean;
  /** Headline above the body. Keep it short ("Smazat trvale?"). */
  title: string;
  /** Body copy explaining what's about to happen. */
  description: ReactNode;
  /** Confirm-button label. Defaults to "Smazat" for the destructive
   *  case; pass a different verb when the action isn't a deletion. */
  confirmLabel?: string;
  /** Cancel-button label. Defaults to "Zrušit". */
  cancelLabel?: string;
  /** Disables the confirm button while the mutation is in flight. */
  busy?: boolean;
  /** Callback when the operator confirms — typically the mutation. */
  onConfirm: () => void;
  /** Callback when Cancel / Escape / backdrop click. */
  onClose: () => void;
  /** Test-id forwarded to the dialog wrapper for E2E targeting. */
  testId?: string;
  /** Slot pro spočítaný dopad (typicky `<ImpactSummary>`); smí přes
   *  `useConfirmVeto` potvrzovací tlačítko sundat. */
  impact?: ReactNode;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  busy = false,
  onConfirm,
  onClose,
  testId,
  impact,
}: Props): JSX.Element | null {
  // Popisky tlačítek byly natvrdo česky, takže anglická administrace
  // ukazovala „Smazat / Zrušit" i po přeložení titulku (fallbackLng je
  // cs). Výchozí hodnoty proto jdou přes i18n; volající je pořád může
  // přebít vlastním slovesem.
  const { t } = useTranslation("common");

  if (!open) return null;

  return (
    <IngotConfirm
      title={title}
      description={description}
      confirmLabel={
        confirmLabel ?? t("confirmDialog.confirm", { defaultValue: "Smazat" })
      }
      cancelLabel={
        cancelLabel ?? t("confirmDialog.cancel", { defaultValue: "Zrušit" })
      }
      closeLabel={t("confirmDialog.close", { defaultValue: "Zavřít" })}
      busy={busy}
      onConfirm={onConfirm}
      onClose={onClose}
      testId={testId}
      impact={impact}
    />
  );
}
